import { LngLatBounds } from 'mapbox-gl'
import { inputTypeFromPort, outputType } from 'common-ui/utils/portModeLabel'
export { inputTypeFromPort, outputType }
import {
  Address,
  Alarm,
  AlarmCause,
  Appliance,
  ApplianceType,
  BuildInfo,
  CoaxPortMode,
  ComprimatoPortMode,
  Coordinates,
  CoordinatesMapbox,
  EntityType,
  Group,
  GroupOrGroupRecipientList,
  Input,
  InputOrInputList,
  InternalDerivedInputPortMode,
  IpcPortMode,
  IpPortMode,
  LimitedAppliance,
  ListResult,
  MatroxPortMode,
  Output,
  OutputOrRecipientList,
  PhysicalPort,
  PortMode,
  PortType,
  Role,
  User,
  VideonPortMode,
} from 'common/api/v1/types'
import { isReleaseBuild } from 'common/buildInfoUtils'
import type { RichOption } from '../components/common/Form/Select'
import type { PaginatedRequestParams } from '../api/nm-types'
import { Routes } from './routes'
import { createElement } from 'react'
import Typography, { TypographyProps } from '@mui/material/Typography'
import cloneDeep from 'lodash/cloneDeep'
import { formatTransformationType } from 'common-ui/utils/portModeLabel'
import { concreteStreamingMode } from 'common/api/v1/helpers'
import { DEFAULT_PAGINATION } from 'common-ui/utils/pagination'

export * from './hooks'
export * from './validation'

export function listResult<TItem>(items: TItem[]): ListResult<TItem> {
  return {
    items: items.slice(),
    total: items.length,
  }
}

/**
 * Returns correct verbal for plural form
 * @param number
 * @param word
 * @param plural - optional, if plural form is not just "<word>s"
 */
export const pluralize = (number: number, word: string, plural = `${word}s`): string =>
  number === 11 || number.toString().slice(-1) !== '1' ? `${number} ${plural}` : `${number} ${word}`

export const pluralizeWord = (number: number, word: string, plural = `${word}s`): string =>
  number === 11 || number.toString().slice(-1) !== '1' ? plural : word

/**
 * Transforms object from form state removing empty strings '' and null, and applying transformations added with parameter config
 * @param form
 * @param config - optional, applying some specific transformation. Watch tests and input/output forms for examples
 * @deprecated Implicit removal of empty strings and nulls leads to surprising behavior in onSubmit handlers.
 * Each form should explicitly handle its own values instead of relying on this function.
 */
export function formTransform(form: any, config?: any, options?: { keepNull?: boolean }): any {
  if (form === null) {
    if (config && config._transform) return config._transform(form)
    return options?.keepNull ? null : undefined
  }
  if (typeof form !== 'object' || Array.isArray(form)) {
    if (config && config._transform) return config._transform(form)
    return form !== '' ? form : undefined
  }

  const transformedForm = config && config._transform ? config._transform(form) : cloneDeep(form)
  if (!transformedForm) return undefined

  return Object.entries<any>(transformedForm).reduce((acc, [key, value]) => {
    if (value === null && options?.keepNull) {
      if (config && config[key] && config[key]._transform)
        return {
          ...acc,
          [key]: config[key]._transform(value),
        }
      return { ...acc, [key]: null }
    }
    if (['', null].includes(value)) {
      if (config && config[key] && config[key]._transform)
        return {
          ...acc,
          [key]: config[key]._transform(value),
        }
      return acc
    }
    if (Array.isArray(value)) {
      return {
        ...acc,
        [key]: value
          .map((item) => formTransform(item, config && config[key], options))
          .filter((e) => e !== null && e !== undefined), // Allow arrays to contain false, number 0 and empty string...
      }
    }

    if (config && config[key] && config[key]._transform)
      return {
        ...acc,
        [key]: formTransform(config[key]._transform(value), { ...config[key], _transform: undefined }, options),
      }
    return {
      ...acc,
      [key]: formTransform(value, config && config[key], options),
    }
  }, {})
}

/**
 * Returns the name for the interface (port) since we have to always show it as conjunctions with the appliance name
 * @param name - interface name
 * @param appliance - appliance object with its name inside
 */
export const getInterfaceName = ({
  name,
  // eslint-disable-next-line @typescript-eslint/no-useless-default-assignment -- default kept intentionally
  appliance: { name: appliance = '' },
}: Pick<PhysicalPort, 'name' | 'appliance'>) => `${appliance}:${name}`

/**
 * Helper to detect if we are in the Stream Manager now
 * @param currentPage - the current page url
 * @param routes - Object containing application routes routes
 */
export const getIsStreamManager = (currentPage: string, routes: Routes) => currentPage.startsWith(routes.stream.route)

/**
 * Adds default pagination values to request parameters
 * @param params
 */
export const withDefaultPagination = <T extends PaginatedRequestParams>(params: Partial<T>) => ({
  ...params,
  pageNumber: params.pageNumber !== undefined ? params.pageNumber : DEFAULT_PAGINATION.pageNumber,
  rowsPerPage: params.rowsPerPage !== undefined ? params.rowsPerPage : DEFAULT_PAGINATION.rowsPerPage,
})

/**
 * Defines if OutputOrRecipientList is Output
 * @param entity - Output | OutputRecipientList
 *
 * Output-specific: `ports` is always present on an Output, but omitted on a derived Input, so do not
 * reuse this shape for inputs. See isInput.
 */
export function isOutput(entity: OutputOrRecipientList): entity is Output {
  return 'ports' in entity
}

/**
 * Defines if InputOrInputList is Input
 * @param entity - Input | InputList
 */
export function isInput(entity: InputOrInputList): entity is Input & { object: EntityType.input } {
  return entity.object === EntityType.input
}

/**
 * Defines if GroupOrGroupRecipientList is group
 * @param entity - Group or GroupRecipientList
 */
export function isGroup(entity: GroupOrGroupRecipientList): boolean {
  return entity.object === EntityType.group
}

/**
 * Coordinates we use and coordinates Mapbox uses differs with its order
 * @param lat
 * @param lng
 */
export const toMapboxCoords = ([lat, lng]: Coordinates): CoordinatesMapbox => [lng, lat]
/**
 * Defines bounds from coordinates array to use in setViewport
 */
export const getBounds = (coordinates: Array<Coordinates>) => {
  const bounds = new LngLatBounds()
  coordinates.forEach((coords) => bounds.extend(toMapboxCoords(coords)))
  return bounds
}

type FormObjValue = string | number | boolean | { [key: string]: FormObjValue } | Array<{ [key: string]: FormObjValue }>
type FormObj = {
  [key: string]: FormObjValue
}
/**
 * Creates default form values for RHF forms to prevent undefined values
 * @param names - array of field names
 * @param bools - array on the field names that should have false instead of '' (checkboxes)
 * @param special - special values to set (defaults for example)
 */
export const createDefaultFiledValues = (names: Array<string>, bools: Array<string>, special: FormObj = {}) =>
  names.reduce<FormObj>((acc, name) => {
    let value
    if (name in special) value = special[name]
    else if (bools.includes(name)) value = false
    else value = ''

    return {
      ...acc,
      [name]: value,
    }
  }, {})

/**
 * Numeric enums has weird transformation into object, you need to use this function instead of just Object.entries
 * @param enumToTransform
 */
export const numericEnum = (enumToTransform: { [key: string]: string | number }) =>
  Object.entries(enumToTransform).reduce<{ [key: string]: number }>(
    (acc, [key, value]) => ({ ...acc, ...(typeof value !== 'number' ? {} : { [key]: value }) }),
    {},
  )

/**
 * Function to get the Typography color to show input/output name based on its adminStatus
 * @param adminStatus
 */
export const inputOutputColor = ({ adminStatus }: Partial<Input | Output>) => ({
  color: adminStatus ? ('textPrimary' as const) : ('textSecondary' as const),
})

/**
 * Whether user can edit appliance
 * @param user - current user
 * @param applianceOwner - appliance's owner id
 */
export const isEditableAppliance = (user: Pick<User, 'group' | 'role'>, applianceOwner: string): boolean =>
  (applianceOwner && (applianceOwner === user.group || user.role === Role.super)) || false

/**
 * Whether user can edit group
 * @param groupId - group id
 * @param user - current user
 */
export const isEditableGroup = (groupId: Group['id'], user: User) =>
  user.role === Role.super || (user.role === Role.admin && user.group === groupId)

/**
 * Whether user can edit interface. Currently only if super user or owner of appliance.
 * @param applianceOwnerId - port's appliance owner id
 * @param user - current user
 */
export const isEditableInterface = (applianceOwnerId: string, user: User) =>
  user.role === Role.super || (user.role === Role.admin && applianceOwnerId === user.group)

/**
 * Whether user can edit output
 * @param ownerId - output's group
 * @param user - current user
 */
export const isEditableOutput = (ownerId: Output['group'], user: User) =>
  user.role === Role.super || (user.role === Role.admin && ownerId === user.group)

export const canEditInput = (user: User, owner: Input['owner']) => {
  return (user.role === Role.admin && user.group === owner) || user.role === Role.super
}

/**
 * Whether user can edit region
 * @param user - current user
 */
export const isEditableRegion = (user: User) => user.role === Role.super

/**
 * If port is editable based on alarm status.
 * This can be called before state is fully loaded and must handle undefined appliance as value.
 * @param appliance
 * @param portId
 */
export const alarmsThatDisablePort = (appliance?: Pick<Appliance, 'alarms'>, portId?: string): Alarm[] | undefined => {
  if (!appliance) return
  const portAlarms = appliance.alarms.filter((alarm) => alarm.physicalPortId === portId)

  const alarmsThatCausePortBeDisabled = portAlarms.filter((portAlarm) => {
    if (portAlarm.alarmCause === AlarmCause.INTERFACE_REMOVED_BUT_IN_USE) return true
  })

  if (alarmsThatCausePortBeDisabled.length > 0) {
    return alarmsThatCausePortBeDisabled
  }

  return
}

export function isCoreNode(applianceType: ApplianceType) {
  return applianceType === ApplianceType.core
}

export function areSetsEqual<T>(s1: Set<T>, s2: Set<T>): boolean {
  if (s1.size !== s2.size) {
    return false
  }
  for (const element of s1) {
    if (!s2.has(element)) {
      return false
    }
  }
  return true
}

/**
 * Helper function to extract current page pathname from hashed URL.
 *
 * /#/url/path?query=params --> returns /url/path
 */
export const getCurrentPage = (): string => {
  return document.location.hash?.slice(1).split('?').shift() || document.location.pathname
}

export function hasAccessToAppliance(appliance: Appliance | LimitedAppliance, user: User) {
  return ('owner' in appliance && user && appliance.owner === user.group) || user.role === Role.super
}

export function inputType(input: Input) {
  const ports = input.ports
  if (input.deriveFrom?.ingestTransform) {
    return [formatTransformationType(input.deriveFrom.ingestTransform.type)]
  }
  if (!ports || !ports.length) {
    return ['Not configured']
  }

  const portModes = Array.from(new Set(ports.map(inputTypeFromPort)))
  return portModes
}

export const makeAddressOptions = (currentAddress: string, addresses: Address[]): Array<RichOption> => {
  const availableAddresses: RichOption[] = addresses.map(({ address, publicAddress }) => ({
    name: `${address}${publicAddress ? ` (${publicAddress})` : ''}`,
    value: address,
  }))
  if (currentAddress && !availableAddresses.find((a) => a.value === currentAddress)) {
    availableAddresses.unshift({
      name: currentAddress,
      disabled: true,
      tooltip: createElement<TypographyProps>(
        Typography,
        { component: 'span', variant: 'body2', color: 'error' },
        '(The current address is not available)',
      ),
    })
  }
  return availableAddresses
}

export const runningDifferentSoftwareVersion = (version?: string, buildInfo?: Pick<BuildInfo, 'release'>): boolean => {
  return buildInfo !== undefined && version !== undefined && version !== buildInfo.release
}

// Edge OS is not built for every backend pipeline, so against a non-release backend the
// appliance OS version essentially never matches buildInfo.release. Only flag the OS as
// outdated when the backend is actually a release build.
export const runningOutdatedOsVersion = (version?: string, buildInfo?: Pick<BuildInfo, 'release'>): boolean => {
  if (!buildInfo || !isReleaseBuild(buildInfo.release)) return false
  return runningDifferentSoftwareVersion(version, buildInfo)
}

export function portTypeFromPortMode(portMode: PortMode): PortType {
  switch (concreteStreamingMode(portMode)) {
    case IpPortMode.rist:
      return PortType.ip
    case IpPortMode.udp:
      return PortType.ip
    case IpPortMode.rtp:
      return PortType.ip
    case IpPortMode.srt:
      return PortType.ip
    case IpPortMode.zixi:
      return PortType.ip
    case IpPortMode.rtmp:
      return PortType.ip
    case IpPortMode.generator:
      return PortType.ip
    case CoaxPortMode.sdi:
      return PortType.coax
    case CoaxPortMode.asi:
      return PortType.coax
    case IpcPortMode.unix:
      return PortType.ip
    case InternalDerivedInputPortMode.internal:
      return PortType.ip
    case VideonPortMode.videonSdi:
      return PortType.videon
    case VideonPortMode.videonHdmi:
      return PortType.videon
    case VideonPortMode.videonAuto:
      return PortType.videon
    case MatroxPortMode.matroxSdi:
      return PortType.coax
    case ComprimatoPortMode.comprimatoSdi:
      return PortType.coax
    case ComprimatoPortMode.comprimatoNdi:
      return PortType.ndi
  }
}
export const COAX_MODES: PortMode[] = [
  ...Object.values(CoaxPortMode),
  ...Object.values(MatroxPortMode),
  ComprimatoPortMode.comprimatoSdi,
]

export const findClosestValue = (target: number, values: number[]): number | undefined => {
  if (values.length === 0) return undefined

  return values.reduce((nearest, current) => {
    const currentDiff = Math.abs(current - target)
    const nearestDiff = Math.abs(nearest - target)
    return currentDiff < nearestDiff ? current : nearest
  })
}
